import React, {
	useEffect,
	useRef,
	useState,
	forwardRef,
	useImperativeHandle
} from 'react';
import { FaMicrophone, FaStop, FaRedo } from 'react-icons/fa';
import { Button } from 'react-bootstrap';

/**
 * @component AudioRecording
 * @description
 * Componente de grabación de audio con control visual.
 * Permite al usuario grabar, detener, regrabar y reproducir su audio, y expone al padre una función
 * (`stopRecordingExternally`) que detiene la grabación y espera que se genere el archivo.
 *
 * Internamente, utiliza `MediaRecorder` y maneja la grabación de forma local con estado visual.
 * También es compatible con flujos asincrónicos como envío desde botones de submit externos.
 *
 * @param {Function} onRecordingComplete - Callback que recibe el archivo `File` generado por la grabación.
 *
 * @returns {JSX.Element}
 *
 * @example
 * <AudioRecording ref={audioRef} onRecordingComplete={(file) => handleAudio(file)} />
 */
const AudioRecording = forwardRef(({ onRecordingComplete }, ref) => {
	const [recording, setRecording] = useState(false);
	const [audioUrl, setAudioUrl] = useState(null);

	const mediaRecorderRef = useRef(null);
	const audioChunksRef = useRef([]);
	const promiseResolverRef = useRef(null); // Resolver para submit externo

	/**
	 * Limpia la URL del audio cuando el componente se desmonta
	 */
	useEffect(() => {
		return () => {
			if (audioUrl) URL.revokeObjectURL(audioUrl);
		};
	}, [audioUrl]);

	/**
	 * Maneja la finalización de la grabación, genera el archivo y lo envía al padre
	 */
	const handleRecordingStop = () => {
		const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
		const file = new File([blob], `audio_${Date.now()}.webm`, { type: 'audio/webm' });
		const url = URL.createObjectURL(blob);

		setAudioUrl(url);
		onRecordingComplete(file);

		if (promiseResolverRef.current) {
			promiseResolverRef.current(file);
			promiseResolverRef.current = null;
		}
	};

	/**
	 * Inicia la grabación desde el micrófono
	 */
	const startRecording = async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			const recorder = new MediaRecorder(stream);
			audioChunksRef.current = [];

			recorder.ondataavailable = (e) => {
				if (e.data.size > 0) audioChunksRef.current.push(e.data);
			};

			recorder.onstop = handleRecordingStop;

			mediaRecorderRef.current = recorder;
			recorder.start();
			setRecording(true);
		} catch (error) {
			alert("No se pudo acceder al micrófono.");
		}
	};

	/**
	 * Detiene la grabación manualmente (desde botón)
	 */
	const stopRecording = () => {
		if (mediaRecorderRef.current && recording) {
			mediaRecorderRef.current.stop();
			setRecording(false);
		}
	};

	/**
	 * Reinicia la grabación eliminando el audio actual
	 */
	const resetRecording = () => {
		setAudioUrl(null);
		onRecordingComplete(null);
		audioChunksRef.current = [];
		mediaRecorderRef.current = null;
		setRecording(false);
	};

	/**
	 * Detiene la grabación desde el padre y retorna el archivo como promesa
	 * @returns {Promise<File|null>}
	 */
	const stopRecordingAsync = () => {
		return new Promise((resolve) => {
			if (mediaRecorderRef.current && recording) {
				promiseResolverRef.current = resolve;
				mediaRecorderRef.current.stop();
				setRecording(false);
			} else {
				resolve(null);
			}
		});
	};

	/**
	 * Expone funciones del componente al padre mediante ref
	 */
	useImperativeHandle(ref, () => ({
		stopRecordingExternally: stopRecordingAsync
	}));

	/**
	 * Renderiza el botón de grabar o regrabar según el estado
	 * @returns {JSX.Element}
	 */
	const renderControls = () => {
		if (!audioUrl) {
			return (
				<Button
					variant={recording ? 'danger' : 'primary'}
					onClick={recording ? stopRecording : startRecording}
				>
					{recording ? <FaStop /> : <FaMicrophone />} {recording ? 'Stop' : 'Record'}
				</Button>
			);
		}

		return (
			<Button variant="warning" onClick={resetRecording}>
				<FaRedo /> Re-record
			</Button>
		);
	};

	return (
		<div className="d-flex flex-column align-items-center">
			<div className="d-flex align-items-center gap-3 mb-2">
				{renderControls()}
			</div>

			{recording && (
				<div className="text-danger fw-bold">Recording...</div>
			)}

			{audioUrl && (
				<audio controls className="w-100">
					<source src={audioUrl} type="audio/webm" />
					Tu navegador no soporta reproducción de audio.
				</audio>
			)}
		</div>
	);
});

export default AudioRecording;